import type { ReactNode } from "react"
import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { IconFileContentFill18, IconFileExportFill18 } from "nucleo-ui-fill-18"

import { Button, IntercomIcon, ZendeskIcon } from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuGroup,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"

import { MetaInfo } from "@/components/meta-info"
import { useTrial } from "@/lib/useTrial"
import { SettingItem } from "@/screens/settings/settings-switches/setting-item"
import { SettingsContainer } from "@/screens/settings/settings-switches/settings-container"
import { SettingsSection } from "@/screens/settings/settings-switches/settings-section"
import { SettingsHeader } from "../settings-header"
import { useChangelogsExport } from "./changelogs-export-button"
import { useContactsExport } from "./contacts-export-button"
import {
	useDocsCsvExport,
	useDocsMdExport,
	useDocsPdfExport,
} from "./docs-export-button"
import { HelpCenterSiteImportDialog } from "./help-center-site-import-dialog"
import {
	importPath,
	ImportPortalDialog,
	useImportPortals,
} from "./import-portal"
import { useThreadsExport } from "./threads-export-button"

function ImportButtons({ children }: { children: ReactNode }) {
	return (
		<div className="flex flex-none flex-wrap justify-end gap-1">{children}</div>
	)
}

interface PendingImport {
	subject: string
	run: (portalInstanceId: string | null) => void
}

export function ImportExportSettingsPage() {
	const navigate = useNavigate()
	const [siteImportPortalId, setSiteImportPortalId] = useState<string | null>(
		null,
	)
	const [siteImportOpen, setSiteImportOpen] = useState(false)
	const { hasTrialEnded } = useTrial()
	const { portals, hasChoice } = useImportPortals()
	const [pending, setPending] = useState<PendingImport | null>(null)

	// One portal means one destination, so the picker never gets in the way.
	const pickPortal = (
		subject: string,
		run: (portalInstanceId: string | null) => void,
	) => {
		if (!hasChoice) {
			run(null)
			return
		}
		setPending({ subject, run })
	}

	const goToImport = (subject: string, path: string) =>
		pickPortal(
			subject,
			(portalInstanceId) =>
				void navigate({ href: importPath(path, portalInstanceId) }),
		)

	const openSiteImport = () =>
		pickPortal("articles", (portalInstanceId) => {
			setSiteImportPortalId(portalInstanceId)
			setSiteImportOpen(true)
		})

	const threadsExport = useThreadsExport()
	const contactsExport = useContactsExport()
	const changelogsExport = useChangelogsExport()
	const docsCsvExport = useDocsCsvExport()
	const docsMdExport = useDocsMdExport()
	const docsPdfExport = useDocsPdfExport()
	const isExportingDocs =
		docsCsvExport.isLoading || docsMdExport.isLoading || docsPdfExport.isLoading

	return (
		<>
			<MetaInfo title="Import/Export" />

			<SettingsHeader
				title="Import / Export"
				subtitle="Bring your existing conversations, contacts, changelog, and docs into Productlane. The Productlane MCP imports data too, so your AI assistant can do it for you."
				docsArticle="8bc833a9-a4a4-4b32-ab1d-a06430a6110d"
			/>

			<SettingsSection title="Import" className="mt-0" />
			<SettingsContainer className="mt-3">
				<SettingItem
					Icon={IntercomIcon}
					title="Intercom"
					onClick={() =>
						goToImport("conversations", "/settings/intercom-import")
					}
					disabled={hasTrialEnded}
				/>
				<SettingItem
					Icon={ZendeskIcon}
					title="Zendesk"
					className="pt-3"
					onClick={() =>
						goToImport("conversations", "/settings/zendesk-import")
					}
					disabled={hasTrialEnded}
				/>
			</SettingsContainer>

			<SettingsContainer className="mt-4">
				<SettingItem
					title="Conversations"
					description="Threads, messages, and replies"
					action={
						<ImportButtons>
							<Button
								variant="ghost"
								size="sm"
								onClick={() =>
									goToImport("conversations", "/settings/csv-import")
								}
								disabled={hasTrialEnded}
							>
								CSV
							</Button>
						</ImportButtons>
					}
				/>
				<SettingItem
					title="Contacts"
					description="People and companies"
					className="pt-3"
					action={
						<ImportButtons>
							<Button
								variant="ghost"
								size="sm"
								onClick={() =>
									void navigate({ to: "/settings/contacts-csv-import" })
								}
								disabled={hasTrialEnded}
							>
								CSV
							</Button>
						</ImportButtons>
					}
				/>
				<SettingItem
					title="Changelog"
					description="Release notes and entries"
					className="pt-3"
					action={
						<ImportButtons>
							<Button
								variant="ghost"
								size="sm"
								onClick={() =>
									goToImport("entries", "/settings/changelog-csv-import")
								}
								disabled={hasTrialEnded}
							>
								CSV
							</Button>
						</ImportButtons>
					}
				/>
				<SettingItem
					title="Docs"
					description="Help center articles"
					className="pt-3"
					action={
						<ImportButtons>
							<Button
								variant="ghost"
								size="sm"
								onClick={openSiteImport}
								disabled={hasTrialEnded}
							>
								Website
							</Button>
							<Button
								variant="ghost"
								size="sm"
								onClick={() =>
									goToImport("articles", "/settings/docs-csv-import")
								}
								disabled={hasTrialEnded}
							>
								CSV
							</Button>
							<Button
								variant="ghost"
								size="sm"
								onClick={() =>
									goToImport("articles", "/settings/docs-markdown-import")
								}
								disabled={hasTrialEnded}
							>
								Markdown
							</Button>
						</ImportButtons>
					}
				/>
			</SettingsContainer>

			<SettingsSection
				title="Export"
				description="Download your data as CSV or Markdown."
			/>
			<SettingsContainer className="mt-3">
				<SettingItem
					title="Conversations"
					action={
						<Button
							variant="ghost"
							size="sm"
							onClick={() => threadsExport.exportNow()}
							isLoading={threadsExport.isLoading}
						>
							Export
						</Button>
					}
				/>
				<SettingItem
					title="Contacts"
					className="pt-3"
					action={
						<Button
							variant="ghost"
							size="sm"
							onClick={() => contactsExport.exportNow()}
							isLoading={contactsExport.isLoading}
						>
							Export
						</Button>
					}
				/>
				<SettingItem
					title="Changelog"
					className="pt-3"
					action={
						<Button
							variant="ghost"
							size="sm"
							onClick={() => changelogsExport.exportNow()}
							isLoading={changelogsExport.isLoading}
						>
							Export
						</Button>
					}
				/>
				<SettingItem
					title="Docs"
					className="pt-3"
					action={
						<DropdownMenu
							contentProps={{ align: "end" }}
							trigger={
								<Button variant="ghost" size="sm" isLoading={isExportingDocs}>
									Export
								</Button>
							}
						>
							<DropdownMenuGroup>
								<DropdownMenuItem
									Icon={IconFileContentFill18}
									onClick={() => docsCsvExport.exportNow()}
								>
									CSV
								</DropdownMenuItem>
								<DropdownMenuItem
									Icon={IconFileExportFill18}
									onClick={() => docsMdExport.exportNow()}
								>
									Markdown
								</DropdownMenuItem>
								<DropdownMenuItem
									Icon={IconFileExportFill18}
									onClick={() => docsPdfExport.exportNow()}
								>
									PDF
								</DropdownMenuItem>
							</DropdownMenuGroup>
						</DropdownMenu>
					}
				/>
			</SettingsContainer>

			<HelpCenterSiteImportDialog
				isOpen={siteImportOpen}
				portalInstanceId={siteImportPortalId}
				onOpenChange={setSiteImportOpen}
			/>

			<ImportPortalDialog
				isOpen={pending !== null}
				subject={pending?.subject ?? "data"}
				portals={portals}
				onOpenChange={(value) => {
					if (!value) setPending(null)
				}}
				onConfirm={(portalInstanceId) => {
					const run = pending?.run
					setPending(null)
					// Let the dialog finish closing before the route changes, so
					// Radix unlocks the body instead of unmounting mid-close.
					requestAnimationFrame(() => run?.(portalInstanceId))
				}}
			/>
		</>
	)
}
